// Pure reorder helpers for the PO line list.
//
// The PO is a FLAT ordered list of lines; `line_section` rows delimit blocks. When the
// user drags a section header, its whole contiguous block (header + following non-section
// lines, up to the next section header) moves as a unit. Dragging a plain line moves only
// that line. Locked `[portal:*]` rows are non-draggable but stay in the id-set so the
// backend reorder (which validates the COMPLETE set) never 409s on a missing id.

export interface ReorderableLine {
  id: number;
  isSection: boolean;
}

/**
 * The contiguous block owned by the section at `startIdx`: the header plus every following
 * line until (but excluding) the next section header. For a non-section line the block is
 * just that single line.
 */
export function blockRange(lines: ReorderableLine[], startIdx: number): [number, number] {
  if (!lines[startIdx]?.isSection) return [startIdx, startIdx];
  let end = startIdx + 1;
  while (end < lines.length && !lines[end].isSection) end++;
  return [startIdx, end - 1];
}

/**
 * Move the block anchored at `activeId` so that it lands at the position of `overId`.
 * Returns the new flat ordering. When `activeId` is a section, its whole block moves; when
 * it's a plain line, only that line moves. Dropping onto a line inside the moving block, or
 * onto the active id itself, is a no-op (returns the same order).
 */
export function moveBlock(
  lines: ReorderableLine[],
  activeId: number,
  overId: number,
): ReorderableLine[] {
  const activeIdx = lines.findIndex((l) => l.id === activeId);
  const overIdx = lines.findIndex((l) => l.id === overId);
  if (activeIdx === -1 || overIdx === -1 || activeId === overId) return lines;

  const [bStart, bEnd] = blockRange(lines, activeIdx);
  // Dropping inside the moving block itself → no-op.
  if (overIdx >= bStart && overIdx <= bEnd) return lines;

  const draggingDown = bStart < overIdx;

  // When the drop target is a section header and we're moving a whole block, snap to the
  // OVER block's boundary so a section never lands between another section's header and its
  // children: dragging up → before the over section; dragging down → after its whole block.
  let targetIdx = overIdx;
  if (lines[overIdx].isSection && lines[bStart].isSection) {
    const [oStart, oEnd] = blockRange(lines, overIdx);
    targetIdx = draggingDown ? oEnd : oStart;
  }
  const targetId = lines[targetIdx].id;

  const block = lines.slice(bStart, bEnd + 1);
  const rest = [...lines.slice(0, bStart), ...lines.slice(bEnd + 1)];

  // Where does the (possibly snapped) target sit in the remaining list?
  const targetInRest = rest.findIndex((l) => l.id === targetId);
  // Insert BEFORE the target when dragging upward, AFTER when dragging downward.
  const insertAt = draggingDown ? targetInRest + 1 : targetInRest;

  return [...rest.slice(0, insertAt), ...block, ...rest.slice(insertAt)];
}

/** The full ordered id list (used as the POST /reorder body). */
export function toOrderedIds(lines: ReorderableLine[]): number[] {
  return lines.map((l) => l.id);
}
